import { useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * CagePinModal — dual control for cage actions that move settled money.
 *
 * Lifted out of CageBalanceSheet so the Drop Count page can use the same prompt
 * rather than growing a second, slightly different one. A security prompt that
 * exists twice is a security prompt that will eventually differ in one of the
 * two places.
 *
 * `req` is null when nothing is being confirmed, otherwise:
 *   { title, subtitle?, needsReason?, reasonLabel?, reasonPlaceholder?,
 *     submitLabel?, onSubmit({ employeeNumber, staffPin, reason }) }
 * onSubmit may throw; the modal stays open and surfaces the message.
 */
export default function CagePinModal({ req, onDone, onError }) {
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [staffPin, setStaffPin] = useState('');
  // Some actions record WHY on the row, so the reason is collected with the PIN
  // rather than as a second prompt.
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  if (!req) return null;

  const needsReason = !!req.needsReason;
  const ready = employeeNumber && staffPin && (!needsReason || reason.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    try {
      await req.onSubmit({ employeeNumber, staffPin, reason: reason.trim() });
      onDone();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Action failed';
      if (onError) onError(err, 'Action failed');
      else toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onDone}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-sm p-5"
      >
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-lg font-semibold text-white">{req.title}</h3>
          <button type="button" onClick={onDone} className="text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </div>
        {req.subtitle && <p className="text-sm text-gray-400 mb-4">{req.subtitle}</p>}
        <label className="block text-xs text-gray-400 mb-1">Employee number</label>
        <input
          autoFocus
          value={employeeNumber}
          onChange={(e) => setEmployeeNumber(e.target.value)}
          className="w-full mb-3 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
        />
        <label className="block text-xs text-gray-400 mb-1">Cage PIN</label>
        <input
          type="password"
          value={staffPin}
          onChange={(e) => setStaffPin(e.target.value)}
          className="w-full mb-4 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
        />
        {needsReason && (
          <>
            <label className="block text-xs text-gray-400 mb-1">{req.reasonLabel || 'Reason'}</label>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={req.reasonPlaceholder || 'e.g. posted twice by mistake'}
              className="w-full mb-4 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
            />
          </>
        )}
        <button
          type="submit"
          disabled={busy || !ready}
          className="w-full px-3 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg disabled:opacity-50"
        >
          {busy ? 'Working…' : req.submitLabel || 'Confirm'}
        </button>
      </form>
    </div>
  );
}
